import React, { useState, forwardRef, useImperativeHandle } from "react";
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Typography,
  Box,
  FormControlLabel,
  Checkbox,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import ImageCropperModal from "./ImageCropperModal";
import { post, postFormData } from "../apis/apis";
import toastAlert from "./toastAlert";

const ImagePicker = forwardRef(({
  user_id_user,
  user_email,
  onSelectImage,
  modalClose,
  initialsBox,
  modalClosingD,
  receiverSide,
  primaryColorComp,
  hideFooter,
  onProfileSaved,
}, ref) => {
  const theme = useTheme();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [selectedImage, setSelectedImage] = useState(null);
  const [loader, setLoader] = useState(false);
  const [imageSrc, setImageSrc] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saveAsProfile, setSaveAsProfile] = useState(false);
  const [loadingImage, setLoadingImage] = useState(false);

  const handleImageChange = async (event) => {
    if (event.target.files && event.target.files.length > 0) {
      setLoadingImage(true);
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = () => {
        setImageSrc(reader.result);
        setModalOpen(true);
        setLoadingImage(false);
      };
      reader.onerror = () => {
        setLoadingImage(false);
        toastAlert("error", "Failed to load image");
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageCropped = (croppedImage) => {
    setLoadingImage(true);
    setSelectedImage(croppedImage);
    setModalOpen(false); // Do not close the main modal
    // Small delay to ensure image is processed
    setTimeout(() => {
      setLoadingImage(false);
    }, 100);
  };
  const base64toFile = (base64String, filename) => {
    const arr = base64String.split(",");
    const mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
  };
  const handleSaveImage = async () => {
    if (!selectedImage) return;
    setLoader(true);
    try {
      console.log("saveAsProfile", saveAsProfile);
      console.log("selectedImage", selectedImage);
      if (saveAsProfile && selectedImage) {
        if (!user_id_user) {
          toastAlert("error", "Cannot save to profile: user not identified. Reload the document and try again.");
        } else {
          try {
            const filename = "profile_signature.png";
            const fileObject = base64toFile(selectedImage, filename);
            const postData = {
              image: fileObject,
              user_id: user_id_user,
            };
            const apiData = await postFormData(postData);
            if (apiData && apiData.error) {
              toastAlert("error", apiData.message || "Error uploading image.");
            } else if (apiData && apiData.public_url) {
              const ImageUrl = apiData.public_url;
              const TypeCheck = initialsBox ? "profile_initils" : "profile";
              const saveSignatureData = {
                user_id: user_id_user,
                signature_image_url: ImageUrl,
                type: TypeCheck,
              };
              const saveResult = await post("user/AddUserSignaturesToDb", saveSignatureData);
              if (saveResult && (saveResult.error === true || saveResult.status === false)) {
                toastAlert("error", saveResult.message || saveResult.error || "Failed to save to profile.");
              } else {
                toastAlert("success", "Signature saved to profile.");
                onProfileSaved?.();
              }
            } else {
              toastAlert("error", "Upload did not return a valid URL.");
            }
          } catch (err) {
            toastAlert("error", err?.message || "Failed to save to profile.");
          }
        }
      }
      onSelectImage(selectedImage);
      modalClose();
    } catch (error) {
      toastAlert("error", "Failed to upload image");
    }
    setLoader(false);
  };

  useImperativeHandle(ref, () => ({
    submit: () => handleSaveImage(),
  }), [selectedImage, saveAsProfile]);

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        flex: 1,
        minHeight: 0,
        position: "relative",
      }}
    >
      {/* Loader overlay when processing image */}
      {(loader || loadingImage) && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            zIndex: 2000,
            borderRadius: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <CircularProgress
              size={40}
              sx={{
                color:
                  primaryColorComp === null || primaryColorComp === undefined
                    ? "rgb(0, 145, 255)"
                    : primaryColorComp,
              }}
            />
            <Typography
              variant="body2"
              sx={{
                color: "rgba(0, 0, 0, 0.7)",
                fontSize: { xs: "12px", sm: "14px" },
              }}
            >
              {loader ? "Processing image..." : "Loading image..."}
            </Typography>
          </Box>
        </Box>
      )}
      {/* Content Area - no scroll, image fits within modal */}
      <Box
        sx={{
          flex: 1,
          width: "100%",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          minHeight: 0,
          pb: 0,
        }}
      >
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          sx={{
            width: "100%",
            flex: 1,
            minHeight: 0,
            pt: 0,
            px: 0,
          }}
        >
          {!selectedImage ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                minHeight: 200,
                border: "2px solid rgba(0, 145, 255, 0.4)",
                borderRadius: 2,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                backgroundColor: "rgb(255, 255, 255)",
              }}
              onClick={() => document.getElementById("fileInput").click()}
            >
              <input
                type="file"
                accept="image/*"
                id="fileInput"
                hidden
                onChange={handleImageChange}
              />
              <UploadIcon
                sx={{
                  fontSize: { xs: 40, sm: 48 },
                  color: "rgb(0, 145, 255)",
                  mb: 2,
                }}
              />
              <Typography
                variant="body1"
                sx={{ color: "rgba(0, 0, 0, 0.6)", textAlign: "center" }}
              >
                Click to upload image
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                flex: 1,
                minHeight: 0,
                borderRadius: 2,
                overflow: "hidden",
                border: "2px solid rgb(0, 145, 255)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
              }}
            >
              <img
                src={selectedImage}
                alt="Selected"
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                }}
              />
              <IconButton
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedImage(null);
                }}
                sx={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  backgroundColor: "rgba(0, 0, 0, 0.6)",
                  color: "#fff",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.8)",
                  },
                  zIndex: 10,
                }}
                size="small"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </Box>
        {!receiverSide && (
          <Box sx={{ minWidth: "fit-content", width: "100%", px: 0 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={saveAsProfile}
                  onChange={(e) => setSaveAsProfile(e.target.checked)}
                  size="small"
                  sx={{
                    color: "rgba(0, 0, 0, 0.6)",
                    "&.Mui-checked": {
                      color: "rgb(35, 179, 232)",
                    },
                  }}
                />
              }
              label={
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: { xs: "13px", sm: "14px" },
                    color: "rgba(0, 0, 0, 0.7)",
                  }}
                >
                  Save as profile {initialsBox ? "initials" : "signature"}
                </Typography>
              }
            />
          </Box>
        )}
      </Box>

      {!hideFooter && (
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "flex-start",
            gap: 0,
            width: "100%",
            px: 0,
            py: 0,
            backgroundColor: "#fff",
            borderTop: "1px solid rgba(0, 0, 0, 0.12)",
            position: "sticky",
            bottom: 0,
            zIndex: 10,
          }}
        >
          <Button
            variant="contained"
            onClick={handleSaveImage}
            disabled={loader || !selectedImage}
            sx={{ bgcolor: "rgb(0, 145, 255)", textTransform: "none" }}
          >
            {loader ? <CircularProgress size={24} color="inherit" /> : "Done"}
          </Button>
        </Box>
      )}
      <Dialog
        open={modalOpen}
        fullScreen={isMobileOrTablet}
        maxWidth={false}
        slotProps={{
          paper: {
            sx: {
              width: isMobileOrTablet ? "100vw" : "90vw",
              maxWidth: isMobileOrTablet ? "100vw" : "1200px",
              height: isMobileOrTablet ? "100vh" : "90vh",
              maxHeight: isMobileOrTablet ? "100vh" : "90vh",
              margin: isMobileOrTablet ? 0 : "auto",
              borderRadius: isMobileOrTablet ? 0 : "8px",
              display: "flex",
              flexDirection: "column",
              padding: 0,
              overflow: "hidden",
              zIndex: 21000,
            }
          },
          backdrop: {
            sx: {
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 20999,
            }
          }
        }}
        sx={{
          zIndex: 21000,
          "& .MuiDialog-container": {
            alignItems: isMobileOrTablet ? "stretch" : "center",
          },
          "& .MuiDialogTitle-root": {
            padding: isMobileOrTablet ? "12px 16px" : "16px 24px",
            width: "100%",
            boxSizing: "border-box",
            flexShrink: 0,
          },
          "& .MuiDialogContent-root": {
            padding: 0,
            width: "100%",
            flex: 1,
            overflow: "hidden",
            boxSizing: "border-box",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <DialogTitle>Crop Image</DialogTitle>
        <DialogContent>
          <ImageCropperModal
            cropSrc={imageSrc}
            onImageCropped={handleImageCropped}
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
});

ImagePicker.displayName = "ImagePicker";

export default ImagePicker;
